import { useTranslations } from '@vocab/react';
import { Box, Stack, Hidden } from 'braid-design-system';

import translations from 'src/App/Components/RecentJobs/.vocab';
import type { DashboardListItemFragment } from 'src/types';
import { getDevContext } from 'src/utils/config';

import JobItem from './JobItem';
import JobItemMobile from './JobItemMobile';
import { Table } from './Table';
import { TableHeaderCell } from './TableHeaderCell';

interface Props {
  jobs: DashboardListItemFragment[];
}

const JobsList = ({ jobs }: Props) => {
  // Make a copy in case we inject jobs from DevContext
  const jobsListCopy = jobs.concat([]);
  const { t } = useTranslations(translations);

  // Add local test job data into the list
  if (getDevContext()?.jobs) {
    getDevContext()?.jobs.forEach((job) => jobsListCopy.unshift(job));
  }

  const tableHeadingItems = [
    {
      id: 'status',
      text: t('Status'),
    },
    {
      id: 'job',
      text: t('Job'),
    },
    {
      id: 'candidates',
      text: t('Candidates'),
    },
    {
      id: 'candidateMatches',
      text: t('Candidate matches'),
    },
    {
      id: 'performance',
      text: t('Performance'),
    },
    {
      id: 'actions',
      text: t('Job actions'),
    },
  ];

  return (
    <>
      <Hidden above="mobile">
        <Stack space="small">
          {jobsListCopy.map((job, index) => (
            <JobItemMobile key={`job-mobile-${index}`} job={job} />
          ))}
        </Stack>
      </Hidden>
      <Hidden below="tablet">
        <Table>
          <Box component="thead">
            <tr>
              {tableHeadingItems.map(
                ({ id, text }: { id: string; text: string }, index: number) => {
                  switch (id) {
                    case 'status':
                      return (
                        <TableHeaderCell
                          paddingLeft="medium"
                          key={`job-header-${index}`}
                        >
                          {text}
                        </TableHeaderCell>
                      );
                    case 'actions':
                      return (
                        <TableHeaderCell
                          paddingRight="medium"
                          align="right"
                          key={`job-header-${index}`}
                        >
                          {text}
                        </TableHeaderCell>
                      );
                    default:
                      return (
                        <TableHeaderCell key={`job-header-${index}`}>
                          {text}
                        </TableHeaderCell>
                      );
                  }
                },
              )}
            </tr>
          </Box>
          <tbody>
            {jobsListCopy.map((job, index) => (
              <JobItem
                key={`job-desktop-${index}`}
                job={job}
                listPosition={index + 1}
              />
            ))}
          </tbody>
        </Table>
      </Hidden>
    </>
  );
};

export default JobsList;
